import { InputEvents, Metrics, ObjectState, SurfaceMapping } from "./types";
import { createCanvasGameLoop } from "./engine";
import { createRootObject } from "./objects/root";
import { createFileObject } from "./objects/file";
import { createCursorObject } from "./objects/cursor";
import { createParagraphFps } from "./objects/paragraphFps";
import { createParagraphObject } from "./objects/paragraph";
import { createWindowObject } from "./objects/window";
import { createRipple } from "./objects/ripple";
import { createBoxObject } from "./objects/box";
import { createIcon } from "./objects/icon";
import { createGameOfLife } from "./objects/gameOfLife";
import { createXaos } from "./objects/xaos";

/*
const rootObject = createRootObject({
  children: [
    createCursorObject({
      x: 0,
      y: 0,
      z: Infinity,
    }),
    createFileObject({
      x: 10,
      y: 10,
      z: 0,
      step: (startTime: number, timestamp: number, inputEvents: InputEvents, metrics: Metrics, inFocus: boolean, objectState: ObjectState) => {
        if (inFocus && inputEvents.clickDetected) {
          const window = createWindowObject({
            x: 40,
            y: 40,
            z: 0,
            w: 50,
            h: 50,
            children: [
              createBoxObject({
                x: 0,
                y: 0,
                z: 0,
                w: 50,
                h: 50,
                direction: "row",
                children: [
                  createParagraphObject({
                    x: 0,
                    y: 0,
                    z: 0,
                    w: 40,
                    h: 20,
                    text: "HELLO",
                  }),
                  createParagraphObject({
                    x: 0,
                    y: 0,
                    z: 0,
                    w: 40,
                    h: 10,
                    text: "WORLD",
                  })
                ]
              }),
            ],
          });
          objectState.add(window);
        }
      }
    }),
    createFileObject({
      x: 30,
      y: 10,
      z: 0,
      step: (startTime: number, timestamp: number, inputEvents: InputEvents, metrics: Metrics, inFocus: boolean, objectState: ObjectState) => {
        if (inFocus && inputEvents.clickDetected) {
          const window = createWindowObject({
            x: 40,
            y: 40,
            z: 0,
            w: 100,
            h: 100,
            children: [
              createRipple({
                x: 0,
                y: 0,
                z: 0,
              })
            ],
          });
          objectState.add(window);
        }
      }
    }),
    createParagraphFps({
      isDraggable: true,
      x: 50,
      y: 10,
      z: 0,
      w: 100,
      h: 10,
    }),
  ]
});
*/


const border = { l: { w: 1 }, r: { w: 1 }, t: { w: 1 }, b: { w: 1 } };

const ripple = createRipple({ id: "ripple" });
const controllerDisplayA = createParagraphObject({ text: `${ripple.getA().toFixed(0)}`, w: 10 * 3, h: 10 });
const controllerDisplayLambda = createParagraphObject({ text: `${ripple.getLambda().toFixed(0)}`, w: 10 * 3, h: 10 });
const controllerDisplayF = createParagraphObject({ text: `${ripple.getF().toFixed(0)}`, w: 10 * 3, h: 10 });
const controllerDisplayAlpha = createParagraphObject({ text: `${ripple.getAlpha().toFixed(0)}`, w: 10 * 3, h: 10 });
const controls = [
  // A controller
  createBoxObject({
    id: "dev.controls.A",
    children: [
      createBoxObject({
        id: "dev.controls.A.top",
        direction: "row",
        children: [
          createIcon({ name: "A" }),
          controllerDisplayA,
        ]
      }),
      createBoxObject({
        id: "dev.controls.A.bottom",
        direction: "row",
        children: [
          createIcon({
            name: "-",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newA = Math.max(0, ripple.getA() - 10);
                ripple.setA(newA);
                controllerDisplayA.setText(`${newA.toFixed(0)}`);
              }
            }
          }),
          createIcon({
            name: "+",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newA = Math.min(999, ripple.getA() + 10);
                ripple.setA(newA);
                controllerDisplayA.setText(`${newA.toFixed(0)}`);
              }
            }
          })
        ]
      }),
    ]
  }),
  // Lambda controller
  createBoxObject({
    id: "dev.controls.lambda",
    children: [
      createBoxObject({
        id: "dev.controls.lambda.top",
        direction: "row",
        children: [
          createIcon({ name: "L" }),
          controllerDisplayLambda,
        ]
      }),
      createBoxObject({
        id: "dev.controls.lambda.bottom",
        direction: "row",
        children: [
          createIcon({
            name: "-",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newLambda = Math.max(0, ripple.getLambda() - 1);
                ripple.setLambda(newLambda);
                controllerDisplayLambda.setText(`${newLambda.toFixed(0)}`);
              }
            }
          }),
          createIcon({
            name: "+",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newLambda = Math.min(999, ripple.getLambda() + 1);
                ripple.setLambda(newLambda);
                controllerDisplayLambda.setText(`${newLambda.toFixed(0)}`);
              }
            }
          })
        ]
      }),
    ]
  }),
  // F controller
  createBoxObject({
    id: "dev.controls.F",
    children: [
      createBoxObject({
        id: "dev.controls.F.top",
        direction: "row",
        children: [
          createIcon({ name: "F" }),
          controllerDisplayF,
        ]
      }),
      createBoxObject({
        id: "dev.controls.F.bottom",
        direction: "row",
        children: [
          createIcon({
            name: "-",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newF = Math.max(0, ripple.getF() - 1);
                ripple.setF(newF);
                controllerDisplayF.setText(`${newF.toFixed(0)}`);
              }
            }
          }),
          createIcon({
            name: "+",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newF = Math.min(999, ripple.getF() + 1);
                ripple.setF(newF);
                controllerDisplayF.setText(`${newF.toFixed(0)}`);
              }
            }
          })
        ]
      }),
    ]
  }),
  // Alpha controller
  createBoxObject({
    id: "dev.controls.Alpha",
    children: [
      createBoxObject({
        id: "dev.controls.A.top",
        direction: "row",
        children: [
          createIcon({ name: "X" }),
          controllerDisplayAlpha,
        ]
      }),
      createBoxObject({
        id: "dev.controls.Alpha.bottom",
        direction: "row",
        children: [
          createIcon({
            name: "-",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newA = Math.max(0, ripple.getAlpha() - 10);
                ripple.setAlpha(newA);
                controllerDisplayAlpha.setText(`${newA.toFixed(0)}`);
              }
            }
          }),
          createIcon({
            name: "+",
            step: (startTime, timestamp, inputEvents, metrics, inFocus) => {
              if (inFocus && inputEvents.clickDetected) {
                const newA = Math.min(999, ripple.getAlpha() + 10);
                ripple.setAlpha(newA);
                controllerDisplayAlpha.setText(`${newA.toFixed(0)}`);
              }
            }
          })
        ]
      }),
    ]
  }),
];
const container = createBoxObject({
  id: "dev.container",
  isDraggable: true,
  w: 200,
  h: 170,
  x: 20,
  y: 30,
  border: { l: { w: 1 }, r: { w: 1 }, t: { w: 1 }, b: { w: 1 } },
  children: [
    createBoxObject({
      id: "dev.header",
      h: 14,
      m: 2,
      border: { b: { w: 1 } },
      direction: "row",
      children: [
        createIcon({
          id: "icon",
          name: "Icon:Close",
          step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
            if (inFocus && inputEvents.clickDetected) {
              objState.remove(container);
            }
          }
        }),
      ]
    }),
    createBoxObject({
      id: "dev.body",
      children: [
        createBoxObject({
          id: "dev.controls",
          h: 10 * 2, // ideally would inherit height from children
          border: { b: { w: 1 } },
          direction: "row",
          children: controls
        }),
        createBoxObject({
          id: "dev.body2",
          children: [
            ripple,
          ]
        }),
      ]
    }),
  ]
});
const file = createFileObject({
  id: "file",
  x: 10,
  y: 10,
  step: (startTime: number, timestamp: number, inputEvents: InputEvents, metrics: Metrics, inFocus: boolean, objectState: ObjectState, surface: Surface, obj: Obj) => {
    if (inFocus && inputEvents.clickDetected) {
      objectState.add(container);
    }
  }
});

const goldenRatio = (1 + Math.sqrt(5)) / 2;

const createRootObject = (surfaceMapping: SurfaceMapping) => {
  const width = surfaceMapping.cols;
  const height = surfaceMapping.rows;

  const menuBarHeight = Math.floor(width * 0.08);

  const menuBar = createBoxObject({
    id: "MenuBar",
    w: width,
    h: menuBarHeight,
    border: { b: { w: 1 } },
    children: [
      createParagraphFps({ id: "Fps", x: width - 60, y: 5, w: 60, h: 10 }),
    ]
  });

  const m = Math.floor(width * 0.05);

  const windowWidth = Math.floor(width * 0.7);
  const windowHeight = Math.floor(windowWidth / goldenRatio);
  const windowHeader = Math.floor(windowWidth * 0.1);

  const gameOfLife = createBoxObject({
    id: "Gol",
    isDraggable: true,
    w: windowWidth,
    h: windowHeight,
    x: m * 2,
    y: menuBar.h + m,
    border,
    children: [
      createBoxObject({
        id: "GolHeader",
        x: m * 2 + 1, // TODO dryme
        y: menuBar.h + m + 1,
        w: windowWidth - 2, // TODO dryme
        h: windowHeader - 1,
        border: { b: { w: 1 } },
        children: [
          createIcon({
            x: m * 2 + 1,
            y: menuBar.h + m + 1,
            id: "icon.gol",
            name: "Icon:Close",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                objState.remove(gameOfLife);
              }
            }
          }),
        ]
      }),
      createGameOfLife({
        x: m * 2 + 1,// TODO dryme
        y: menuBar.h + m + 1 + windowHeader + 1, // TODO dryme
        z: 1,
        w: windowWidth - 2, // TODO dryme
        h: windowHeight - 2 - windowHeader - 1, // TODO dryme
        id: "GolMain",
      })
    ]
  });

  const fileGol = createFileObject({
    id: "FileGol",
    x: m,
    y: menuBar.h + m,
    step: (startTime: number, timestamp: number, inputEvents: InputEvents, metrics: Metrics, inFocus: boolean, objectState: ObjectState, surface: Surface, obj: Obj) => {
      if (inFocus && inputEvents.clickDetected) {
        objectState.add(gameOfLife);
      }
    }
  });

  const xaosCloseIcon = createIcon({
    x: m * 2 + 1,
    y: menuBar.h + m + 1,
    id: "xaos.close",
    name: "Icon:Close",
    step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
      if (inFocus && inputEvents.clickDetected) {
        objState.remove(xaos);
      }
    }
  });

  const xaosComponent = createXaos({
    x: m * 2 + 1,// TODO dryme
    y: menuBar.h + m + 1 + windowHeader + 1, // TODO dryme
    z: 1,
    w: windowWidth - 2, // TODO dryme
    h: windowHeight - 2 - windowHeader - 1, // TODO dryme
    id: "XaosMain",
  });

  const xaos = createBoxObject({
    id: "Xaos",
    isDraggable: true,
    w: windowWidth,
    h: windowHeight,
    x: m * 2,
    y: menuBar.h + m,
    border,
    children: [
      createBoxObject({
        id: "XaosHeader",
        x: m * 2 + 1, // TODO dryme
        y: menuBar.h + m + 1,
        w: windowWidth - 2, // TODO dryme
        h: windowHeader - 1,
        border: { b: { w: 1 } },
        children: [
          xaosCloseIcon,
          createIcon({
            x: m * 2 + 1 + xaosCloseIcon.w,
            y: menuBar.h + m + 1,
            id: "xaos.restart",
            name: "Icon:Restart",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.restart();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 2 * xaosCloseIcon.w, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.pause",
            name: "Icon:Pause",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.pause();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 3 * xaosCloseIcon.w, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.play",
            name: "Icon:Play",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.play();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 4 * xaosCloseIcon.w, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.presetLeft",
            name: "<",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.presetDec();
              }
            }
          }),
          createParagraphObject({
            w: 10 * 4,
            h: xaosCloseIcon.h,
            x: m * 2 + 1 + 5 * xaosCloseIcon.w, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.presetValue",
            text: "0000",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objectState, surface, obj) => {
              const text = `${xaosComponent.getPresetIdx().toFixed(0).padStart(4, "0")}`;
              obj.setText(text);
            }
          }),
          createIcon({
            x: m * 2 + 1 + 6 * xaosCloseIcon.w + 10 * 3, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.presetRight",
            name: ">",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.presetInc();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 7 * xaosCloseIcon.w + 10 * 3, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.rotateLeft",
            name: "Icon:Left",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.rotate3DLeft();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 8 * xaosCloseIcon.w + 10 * 3, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.rotateUp",
            name: "Icon:Up",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.rotate3DUp();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 9 * xaosCloseIcon.w + 10 * 3, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.rotateDown",
            name: "Icon:Down",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.rotate3DDown();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 10 * xaosCloseIcon.w + 10 * 3, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.rotateRight",
            name: "Icon:Right",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.rotate3DRight();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 11 * xaosCloseIcon.w + 10 * 3, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.rotateLeft_",
            name: "Icon:CurveArrowLeft",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.rotate2DLeft();
              }
            }
          }),
          createIcon({
            x: m * 2 + 1 + 12 * xaosCloseIcon.w + 10 * 3 + 2, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.rotateRight_",
            name: "Icon:CurveArrowRight",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objState) => {
              if (inFocus && inputEvents.clickDetected) {
                xaosComponent.rotate2DRight();
              }
            }
          }),
          createParagraphObject({
            w: 10 * 4,
            h: xaosCloseIcon.h,
            x: m * 2 + 1 + 13 * xaosCloseIcon.w + 10 * 3 + 2, // TODO dryme
            y: menuBar.h + m + 1,
            id: "xaos.cubeView",
            text: "MN",
            step: (startTime, timestamp, inputEvents, metrics, inFocus, objectState, surface, obj) => {
              const text = xaosComponent.getOrientationState();
              obj.setText(text);
            }
          }),
        ]
      }),
      xaosComponent,
    ]
  });

  const fileXaos = createFileObject({
    id: "FileXaos",
    x: fileGol.w + m * 2,
    y: menuBar.h + m,
    step: (startTime: number, timestamp: number, inputEvents: InputEvents, metrics: Metrics, inFocus: boolean, objectState: ObjectState, surface: Surface, obj: Obj) => {
      if (inFocus && inputEvents.clickDetected) {
        objectState.add(xaos);
      }
    }
  });

  const main = createBoxObject({
    id: "Main",
    y: menuBarHeight,
    w: width,
    h: height - menuBarHeight,
    children: [
      fileGol,
      fileXaos
    ]
  });

  const rootObject = createBoxObject({
    id: "$",
    children: [
      createCursorObject({ id: "Cursor" }),
      createBoxObject({
        id: "Body",
        children: [
          menuBar,
          main,
        ],
      })
    ]
  });

  return rootObject;
}

createCanvasGameLoop("gfx", createRootObject);
